import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { PermissionsService } from '@app/areas/permissions/services/permissions.service';
import { SetPermissions } from '@app/_store/app.actions';
import { Store } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class PermissionsModuleGuard  {

  public static readonly rerouteChainStateKey = 'permissionsModuleGuardRerouteChain';
  public static readonly maxRerouteChainLength = 10;

  constructor(
    private permissionService: PermissionsService,
    private store: Store,
    private router: Router
  ) { }

  // Gets necessary permission data via http and adds them to state
  // If no master permission is set then this acts as a dummy guard to load the data and does not decide if the user can navigate to any route or not
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {

    const master: number = route.data.permissionsRequired;
    const permissions: number[] = [...(route.data.permissions || [])];
    if (master && !permissions.includes(master)) {
      permissions.push(master);
    }

    if (permissions?.length) {

      return this.permissionService.getPermissions(permissions).pipe(
        map(response => {

          if (master) {
            const modulePermission: number = response?.find(p => p.permissionFieldID === master)?.fieldPermission;
            // This is 0 or 4
            if (!modulePermission) {
              return this.permissionDenied(route, state);
            }
          }

          // Success
          this.store.dispatch(new SetPermissions(response));
          return true;

        }),
        catchError(error => {

          if (master) {
            // Preserve the existing error behaviour. Reroute is only for an actual permission denial.
            this.noPermission();
            return of(false);
          }

          // Let trought even when errors occures if no master permission is set, since this guard doesn't decide anything then
          return of(true);
        })
      );

    } else {
      return of(true);
    }
  }

  // Clears the current module permissions when exiting that module
  // E.g. clears the permissions which are generated by the upper canActivate-hook
  canDeactivate(): boolean | Observable<boolean> {
    this.store.dispatch(new SetPermissions(null));
    return true;
  }

  private permissionDenied(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    const reroute: string = typeof route.data.reroute === 'string' ? route.data.reroute.trim() : '';

    if (reroute){
      if(this.tryReroute(reroute, state))
        return true;
    }

    this.noPermission();
    return false;
  }

  private tryReroute(reroute: string, state: RouterStateSnapshot): boolean {
    const currentUrl = this.normalizeUrl(state.url);
    const targetUrl = this.normalizeUrl(state.url + "/" + reroute);
    const navigationState = this.router.getCurrentNavigation()?.extras.state || {};
    const existingChain = navigationState[PermissionsModuleGuard.rerouteChainStateKey];
    const rerouteChain: string[] = Array.isArray(existingChain)
      ? existingChain.filter(value => typeof value === 'string')
      : [];

    const createsLoop = targetUrl === currentUrl || rerouteChain.includes(targetUrl);
    const chainTooLong = rerouteChain.length >= PermissionsModuleGuard.maxRerouteChainLength;

    if (createsLoop || chainTooLong) {
      return false;
    }

    void this.router.navigateByUrl(targetUrl, {
      state: {
        ...navigationState,
        [PermissionsModuleGuard.rerouteChainStateKey]: [...rerouteChain, currentUrl]
      }
    });

    return true;
  }

  private normalizeUrl(url: string): string {
    return this.router.parseUrl(url).toString();
  }

  noPermission() {
    this.router.navigate(['no-access']);
  }

}
